import React from 'react';
import { Label } from '../../ui/label';
import QuestionField from './QuestionField';
import { evaluateShowWhen } from './showWhen';
import { getSurveyIconComponent } from './surveyIcons';
import SurveyNumberStepper from './SurveyNumberStepper';

const hasQuestionValue = (question, value, stepSlug) => {
  if (!question?.required) return true;
  if (question.type === 'repeater') {
    const minLen = stepSlug === 'commuting' ? Math.max(question.min ?? 0, 1) : question.min || 1;
    return Array.isArray(value) && value.length >= minLen;
  }
  if (question.type === 'weekly_toggle') return true;
  return !(value == null || value === '');
};

const SurveyStep = ({ questions, answers, onAnswerChange, showValidation, stepSlug }) => {
  const visibleQuestions = questions.filter((question) => evaluateShowWhen(question.showWhen, answers));

  const equipmentQuestions =
    stepSlug === 'wfh-equipment'
      ? visibleQuestions.filter((question) => question.type === 'integer' && getSurveyIconComponent(question.icon))
      : [];

  const useEquipmentPrototypeLayout = equipmentQuestions.length > 0;
  const equipmentKeySet = new Set(equipmentQuestions.map((q) => q.key));
  const restQuestions = useEquipmentPrototypeLayout
    ? visibleQuestions.filter((question) => !equipmentKeySet.has(question.key))
    : visibleQuestions;

  if (useEquipmentPrototypeLayout) {
    return (
      <div className='space-y-8'>
        <div>
          <div className='text-sm font-semibold mb-1.5'>Equipment</div>
          <div className='space-y-2'>
            {equipmentQuestions.map((question) => {
              const currentValue = answers[question.key];
              const valid = hasQuestionValue(question, currentValue, stepSlug);
              const showError = showValidation && !valid;
              const Icon = getSurveyIconComponent(question.icon);
              const imin = question.min != null ? Number(question.min) : 0;
              const imax = question.max != null ? Number(question.max) : 99;
              return (
                <div key={question.key} className='space-y-1'>
                  <div className='flex items-center gap-3 rounded-xl border border-border p-3'>
                    {Icon ? (
                      <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-accent shrink-0'>
                        <Icon size={18} strokeWidth={1.75} />
                      </div>
                    ) : null}
                    <div className='flex-1 text-sm font-medium min-w-0 leading-snug'>
                      {question.question}
                      {question.required ? <span className='text-destructive ml-0.5'>*</span> : null}
                    </div>
                    <SurveyNumberStepper
                      value={currentValue}
                      onChange={(next) => onAnswerChange(question.key, next)}
                      min={imin}
                      max={imax}
                      compact
                    />
                  </div>
                  {showError ? <div className='text-xs text-destructive'>This field is required.</div> : null}
                </div>
              );
            })}
          </div>
          <div className='text-[11px] text-muted-foreground mt-1.5'>0 if you don&apos;t use that type</div>
        </div>

        {restQuestions.map((question) => {
          const currentValue = answers[question.key];
          const valid = hasQuestionValue(question, currentValue, stepSlug);
          const showError = showValidation && !valid;
          const QIcon = getSurveyIconComponent(question.icon);
          const showLabelIcon = QIcon && question.type !== 'integer' && question.type !== 'decimal';
          return (
            <div key={question.key} className='space-y-2'>
              <Label className='text-sm font-semibold flex items-start gap-2.5'>
                {showLabelIcon ? (
                  <span className='w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5'>
                    <QIcon size={18} strokeWidth={1.5} />
                  </span>
                ) : null}
                <span className='min-w-0 flex-1 leading-snug'>
                  {question.question}
                  {question.required ? <span className='text-destructive ml-1'>*</span> : null}
                </span>
              </Label>
              <QuestionField
                question={question}
                answers={answers}
                value={currentValue}
                onChange={(nextValue) => onAnswerChange(question.key, nextValue)}
                stepSlug={stepSlug}
              />
              {question.hint ? <div className='text-[11px] text-muted-foreground mt-1.5'>{question.hint}</div> : null}
              {showError ? <div className='text-xs text-destructive'>This field is required.</div> : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {visibleQuestions.map((question) => {
        const currentValue = answers[question.key];
        const valid = hasQuestionValue(question, currentValue, stepSlug);
        const showError = showValidation && !valid;
        const QIcon = getSurveyIconComponent(question.icon);
        const showLabelIcon = QIcon && question.type !== 'integer' && question.type !== 'decimal';
        return (
          <div key={question.key} className='space-y-2'>
            <Label className='text-sm font-medium flex items-start gap-2.5'>
              {showLabelIcon ? (
                <span className='w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5'>
                  <QIcon size={18} strokeWidth={1.5} />
                </span>
              ) : null}
              <span className='min-w-0 flex-1 leading-snug'>
                {question.question}
                {question.required ? <span className='text-destructive ml-1'>*</span> : null}
              </span>
            </Label>
            {question.hint ? <div className='text-[11px] text-muted-foreground mt-1'>{question.hint}</div> : null}
            <QuestionField
              question={question}
              answers={answers}
              value={currentValue}
              onChange={(nextValue) => onAnswerChange(question.key, nextValue)}
              stepSlug={stepSlug}
            />
            {showError ? <div className='text-xs text-destructive'>This field is required.</div> : null}
          </div>
        );
      })}
    </div>
  );
};

export const isStepValid = (questions, answers, stepSlug) =>
  questions
    .filter((question) => evaluateShowWhen(question.showWhen, answers))
    .every((question) => hasQuestionValue(question, answers[question.key], stepSlug));

export default SurveyStep;
